import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-primary-50 overflow-x-hidden" }
const _hoisted_2 = { class: "container mx-auto px-4 py-6 max-w-6xl overflow-hidden" }
const _hoisted_3 = { class: "mb-6" }
const _hoisted_4 = { class: "text-display-sm font-heading text-primary-800 mb-1" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-primary-500" }, " Here's what's happening in Foxwood Crossing ", -1)
const _hoisted_6 = { class: "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" }
const _hoisted_7 = { class: "grid lg:grid-cols-3 gap-6" }
const _hoisted_8 = { class: "lg:col-span-2" }
const _hoisted_9 = { class: "space-y-6" }
const _hoisted_10 = { class: "w-full max-w-2xl" }
const _hoisted_11 = { class: "w-full max-w-2xl max-h-[90vh] overflow-y-auto" }

import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { usePostStore } from "@/stores/posts";
import { useAuthStore } from "@/stores/auth";
import { useVoteStore } from "@/stores/votes";
import { HoodApi } from "@/services/HoodApi";

import WeatherWidget from "@/components/dashboard/WeatherWidget.vue";
import TrashWidget from "@/components/dashboard/TrashWidget.vue";
import YardWasteWidget from "@/components/dashboard/YardWasteWidget.vue";
import LeafCollectionWidget from "@/components/dashboard/LeafCollectionWidget.vue";
import EventsCountWidget from "@/components/dashboard/EventsCountWidget.vue";
import LocalEventsFeed from "@/components/dashboard/LocalEventsFeed.vue";
import NeighborPostsFeed from "@/components/dashboard/NeighborPostsFeed.vue";
import QuickLinks from "@/components/dashboard/QuickLinks.vue";
import CommunityActivity from "@/components/dashboard/CommunityActivity.vue";
import CreatePostForm from "@/components/posts/CreatePostForm.vue";
import PostDetailModal from "@/components/posts/PostDetailModal.vue";

// Onboarding
import WelcomeModal from "@/components/onboarding/WelcomeModal.vue";
import OnboardingChecklist from "@/components/onboarding/OnboardingChecklist.vue";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const router = useRouter();

// Dashboard widgets
const postStore = usePostStore();
const authStore = useAuthStore();
const voteStore = useVoteStore();

// State
const posts = computed(() => postStore.active_posts);
const firstName = computed(() => authStore.user_details.first_name);
const postsLoading = ref(true);
const showCreatePost = ref(false);
const selectedPost = ref<any>(null);

// Quick stats from API
const quickStats = ref({
  total_houses: 166,
  active_proposals: 0,
  upcoming_events: 0,
});

const upcomingEventsCount = ref(0);
const activeVotesCount = computed(() => voteStore.active_votes?.length || 0);

// Time of day greeting
const timeOfDay = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
});

// Handlers
const handlePostCreated = async () => {
  showCreatePost.value = false;
  await postStore.fetch_posts();
};

const handleViewPost = (post: any) => {
  selectedPost.value = post;
};

const handleCommentAdded = (postId: number) => {
  // Update selectedPost from the refreshed store
  const updatedPost = postStore.posts.find((p) => p.id === postId);
  if (updatedPost) {
    selectedPost.value = updatedPost;
  }
};

// Fetch data
const fetchDashboardData = async () => {
  try {
    const [feed] = await Promise.all([
      HoodApi.fetch_homepage_feed(),
      voteStore.get_votes(),
    ]);

    quickStats.value = feed.quick_stats;
    upcomingEventsCount.value = feed.upcoming_events?.length || 0;
  } catch (err) {
    console.error("Failed to fetch dashboard data:", err);
  }
};

const fetchPosts = async () => {
  try {
    await postStore.fetch_posts();
  } catch (err) {
    console.error("Failed to fetch posts:", err);
  } finally {
    postsLoading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([fetchDashboardData(), fetchPosts()]);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h1", _hoisted_4, " Good " + _toDisplayString(_unref(timeOfDay)) + ", " + _toDisplayString(_unref(firstName)), 1),
        _hoisted_5
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(WeatherWidget),
        _createVNode(TrashWidget),
        _createVNode(YardWasteWidget),
        _createVNode(LeafCollectionWidget),
        _createVNode(EventsCountWidget, { count: upcomingEventsCount.value }, null, 8, ["count"])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(NeighborPostsFeed, {
            posts: _unref(posts),
            loading: postsLoading.value,
            onCreatePost: _cache[0] || (_cache[0] = ($event: any) => (showCreatePost.value = true)),
            onViewPost: handleViewPost,
            onViewAll: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push('/posts')))
          }, null, 8, ["posts", "loading"])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(QuickLinks),
          _createVNode(OnboardingChecklist),
          _createVNode(LocalEventsFeed),
          _createVNode(CommunityActivity, {
            "proposal-count": quickStats.value.active_proposals,
            "vote-count": _unref(activeVotesCount)
          }, null, 8, ["proposal-count", "vote-count"])
        ])
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (showCreatePost.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4",
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (showCreatePost.value = false), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_10, [
              _createVNode(CreatePostForm, {
                onPostCreated: handlePostCreated,
                onClose: _cache[2] || (_cache[2] = ($event: any) => (showCreatePost.value = false))
              })
            ])
          ]))
        : _createCommentVNode("", true)
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (selectedPost.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4",
            onClick: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (selectedPost.value = null), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_11, [
              _createVNode(PostDetailModal, {
                post: selectedPost.value,
                onClose: _cache[4] || (_cache[4] = ($event: any) => (selectedPost.value = null)),
                onCommentAdded: handleCommentAdded,
                onPostDeleted: _cache[5] || (_cache[5] = ($event: any) => (selectedPost.value = null))
              }, null, 8, ["post"])
            ])
          ]))
        : _createCommentVNode("", true)
    ])),
    _createVNode(WelcomeModal, {
      onGetStarted: _cache[7] || (_cache[7] = ($event: any) => (_unref(router).push('/profile')))
    })
  ]))
}
}

})